import React, { useCallback, useState, useEffect } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import './autocomplete.module.css';
import { Specialty } from 'types/specialty';
import { toast } from 'react-toastify';
import { api } from 'services/api';
import SpecialtyItem from './SpecialtyItem';

let timer: NodeJS.Timeout;

interface AutocompleteFromProps {
  onSelect(value: Specialty | null): void;
  label: string;
  placeholder: string;
  selected: Specialty | null;
  helperText?: string;
  error?: boolean;
}

const SpecialtiesAutoComplete: React.FC<AutocompleteFromProps> = ({
  onSelect,
  label,
  placeholder,
  selected,
  helperText = '',
  error = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [filtered, setFiltered] = useState<Specialty[]>([]);
  const [options, setOptions] = useState<Specialty[]>([]);

  const handleSelect = useCallback(
    (option: Specialty) => {
      onSelect(option);
      setFiltered([]);
    },
    [onSelect],
  );

  useEffect(() => {
    setFiltered(options);
  }, [options]);

  const findSpecialties = useCallback(async (term: string) => {
    setLoading(true);
    api
      .get('/specialties/all', { params: { name: term, active: 1 } })
      .then(response => {
        setOptions(response.data);
      })
      .catch(err => {
        toast.error('Ocorreu um erro ao buscar as especialidades');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  function handleChange(value: string) {
    clearTimeout(timer);

    if (value.length < 2) {
      return;
    }

    timer = setTimeout(() => findSpecialties(value), 400);
  }

  return (
    <Autocomplete
      fullWidth
      value={selected}
      options={filtered}
      noOptionsText="Nenhuma especialidade encontrada"
      getOptionLabel={option => option.name}
      onChange={(event, newValue) => onSelect(newValue)}
      renderOption={(props, option) => (
        <SpecialtyItem {...props} key={option.id} handleSelect={handleSelect} option={option} selected={selected} />
      )}
      renderInput={params => (
        <TextField
          {...params}
          onBlur={() => setFiltered([])}
          onChange={e => handleChange(e.target.value)}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText || 'Digite 2 ou mais caracteres'}
          fullWidth
        />
      )}
    />
  );
};

export default SpecialtiesAutoComplete;
